import React, { useState, useEffect } from 'react'
import { useHistory, withRouter } from "react-router-dom";
import {
    Modal,
    Select,
    Form,
    Row,
    Col,
    Typography,
    message,
    Button,
} from "antd";
import { platformApi } from "../../../../api";
// import './index.less'

const { Text, Title } = Typography;
const { Option } = Select;

const EmployeeTerminate = ({
    form,
    open,
    close,
    data,
    employee,
    setNewEmployee,
    editData,
    editable,
    modify,
}) => {
    const history = useHistory();
    const { getFieldDecorator, getFieldValue, setFieldsValue } = form;
    const [error, setError] = useState({});
    const [ecid, setEcid] = useState('');
    const [branchWarning, setBranchWarning] = useState(null);
    const [roleError, setRoleError] = useState(null);

    useEffect(() => {
        // Reset warnings/errors when modal opens/closes or data changes
        setBranchWarning(null);
        setRoleError(null);
        setEcid('');
    }, [open, data]);

    // Get the deactivated employee's branch IDs
    const getDeactivatedBranches = () => {
        if (!data || !data.profile || !data.profile.branch) return [];
        return data.profile.branch; // array of { id, name }
    };

    const handleEmployee = async (selectedId) => {
        setEcid(selectedId);
        setFieldsValue({ employee: selectedId });
        setBranchWarning(null);
        setRoleError(null);

        // Find the selected replacement employee object
        const selectedEmployee = employee.find(e => e.id === selectedId);
        if (!selectedEmployee) return;

        // 1. Role validation (enforce matching department role, bypass if deactivated user or replacement is a CEO)
        const targetRole = data?.profile?.department?.role;
        const isTargetCEO = targetRole && targetRole.toUpperCase() === "CEO";

        const replacementRole = selectedEmployee.profile?.department?.role;
        const isReplacementCEO = replacementRole && replacementRole.toUpperCase() === "CEO";

        if (targetRole && !isTargetCEO && !isReplacementCEO) {
            if (replacementRole !== targetRole) {
                const errorMsg = `Please select a replacement employee who belongs to the ${targetRole} department.`;
                setRoleError(errorMsg);
                message.error(errorMsg, 2.5);
                return;
            }
        }

        // 2. Branch validation
        const selectedBranches = (selectedEmployee.profile?.branch || []).map(b => b.id);
        const deactivatedBranches = getDeactivatedBranches();

        // Find branches of the deactivated employee that the replacement doesn't have
        const missingBranches = deactivatedBranches.filter(
            b => !selectedBranches.includes(b.id)
        );

        if (missingBranches.length > 0) {
            const missingNames = missingBranches.map(b => b.name).join(', ');
            const warningMsg = `${selectedEmployee.profile?.employeeName || 'This employee'} is not a part of: ${missingNames}. Cannot reassign ramps in these branches.`;
            setBranchWarning(warningMsg);
            message.error(warningMsg, 2.5);
            return;
        }

        // 3. Ramp assignment check (only if deactivated employee is currently assigned to a ramp AND replacement is a Mechanic)
        const isSelectedMechanic = replacementRole === "Mechanic";
        if (isSelectedMechanic) {
            try {
                const res = await platformApi.get("/api/ramp");
                if (res && res.data && res.data.code === 200) {
                    const branchDataList = res.data.response || [];

                    // Check if the deactivated employee is currently assigned to any ramp
                    let deactivatedAssignedRamp = null;
                    for (const branchData of branchDataList) {
                        const ramps = branchData.ramps || [];
                        const found = ramps.find(r => r.mechanic && r.mechanic.id === data.id);
                        if (found) {
                            deactivatedAssignedRamp = found;
                            break;
                        }
                    }

                    // Only run conflict validation if the deactivated mechanic has a ramp to reassign
                    if (deactivatedAssignedRamp) {
                        let assignedRampNo = null;
                        let assignedBranchName = null;

                        for (const branchData of branchDataList) {
                            const ramps = branchData.ramps || [];
                            const assignedRamp = ramps.find(r => r.mechanic && r.mechanic.id === selectedId);
                            if (assignedRamp) {
                                assignedRampNo = assignedRamp.rampNo;
                                assignedBranchName = assignedRamp.branch?.name || branchData.branchId;
                                break;
                            }
                        }

                        if (assignedRampNo) {
                            const warningMsg = `${selectedEmployee.profile?.employeeName || 'This employee'} is already assigned to Ramp ${assignedRampNo} in branch ${assignedBranchName}.`;
                            setRoleError(warningMsg);
                            message.error(warningMsg, 2.5);
                        }
                    }
                }
            } catch (err) {
                console.error("Error checking ramp assignment: ", err);
            }
        }
    };

    const handleSubmit = () => {
        // Block save if selected employee has role mismatch
        if (roleError) {
            message.error(roleError, 2.5);
            return;
        }
        // Block save if selected employee has branch mismatch
        if (branchWarning) {
            message.error(branchWarning, 2.5);
            return;
        }
        form.validateFields(async (validationError) => {
            if (validationError === null) {
                if (data) {
                    setNewEmployee(getFieldValue("employee") ? getFieldValue("employee") : ecid);
                    close();
                }
            }
        });
    };

    const clearFieldsAndClose = () => {
        form.resetFields();
        setEcid('');
        setBranchWarning(null);
        setRoleError(null);
        setNewEmployee('');
        close();
    };

    return (
        <Modal
            title={<Title level={4}>Employee ReAssignment</Title>}
            visible={open}
            onCancel={clearFieldsAndClose}
            maskClosable={false}
            keyboard={false}
            okText={!editable ? "Modify" : "Save"}
            okButtonProps={{ disabled: (!editable && !modify) }}
            onOk={() => (editable ? handleSubmit() : setEditable(true))}
        >
            <Form className="purchase-form">
                {data && data.profile && (
                    <Row style={{ marginBottom: 12 }}>
                        <Col span={24}>
                            <Text type="secondary">
                                Deactivating: <Text strong>{data.profile.employeeName}</Text>
                                {data.profile.branch && data.profile.branch.length > 0 && (
                                    <span> — Branches: <Text strong>{data.profile.branch.map(b => b.name).join(', ')}</Text></span>
                                )}
                            </Text>
                        </Col>
                    </Row>
                )}
                <Row type="flex" justify="space-between">
                    <Col span={24}>
                        <Form.Item
                            style={{ display: "flex", width: "100%", alignItems: "center" }}
                            labelCol={{ style: { width: "140px", flexShrink: 0, textAlign: "left", lineHeight: "normal" } }}
                            wrapperCol={{ style: { flexGrow: 1 } }}
                            colon={false}
                            required
                            validateStatus={error.EMPLOYEE && error.EMPLOYEE.type}
                            help={error.EMPLOYEE && error.EMPLOYEE.message}
                            label={<Text>Reassign Work To</Text>}
                        >
                            {getFieldDecorator("employee", {
                                rules: [
                                    {
                                        required: true,
                                        message: "Select Employee",
                                    },
                                ],
                            })(
                                <Select
                                    style={{ width: '100%' }}
                                    dropdownStyle={{
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                    }}
                                    showSearch
                                    showAction={["click", "focus"]}
                                    disabled={!editable}
                                    onChange={(event) => {
                                        handleEmployee(event)
                                    }}
                                    placeholder="Select replacement employee"
                                    filterOption={(input, option) =>
                                        option.props.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {employee.length && employee.map((obj) => (
                                        <Select.Option key={obj.id} value={obj.id}>
                                            {obj.profile && obj.profile.employeeName
                                                ? obj.profile.employeeName
                                                : obj.profile?.employeeId}
                                        </Select.Option>
                                    ))}
                                </Select>
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
}

const WrappedEmployeeTerminate = Form.create("employee_terminate")(
    EmployeeTerminate
);

export default WrappedEmployeeTerminate;